import React, { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import orderBy from "lodash.orderby";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import { updateStandaloneWsForm, updateUserScreen } from "../../redux/actions";
import { masterTrackGaEvent } from "../../services/ga";
import { config } from "../../config/config";
import SigviewTooltip from "../Common/SigviewTooltip";
import "./SidenavDrawer.scss";
import { initialWsFormScreenValue } from "../../utils/analyzeUtils";
import SigviewVR from "../Common/SigviewVR";
import openxSvgNewLogo from "../../../assets/images/openxSvgNewLogo.svg";
import Overlay from "../Overlay/Overlay";
import OverlayContainer from "../Overlay/OverlayContainer";

const useStyles = makeStyles((theme) => ({
  drawer: {
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  paper: {
    boxShadow: "4px 0px 6px 0px rgba(0, 0, 0, 0.06)",
    border: "none",
    justifyContent: "space-between",
    overflowX: "hidden",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  hide: {
    width: 0,
    overflow: "hidden",
    transition: theme.transitions.create(["width"], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.complex,
    }),
  },
  show: {
    width: "100%",
    transition: theme.transitions.create(["width"], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.complex,
    }),
  },
  drawerOpen: {
    width: theme.spacing(30),
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.complex,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.complex,
    }),
    overflowX: "hidden",
    width: theme.spacing(7) + 1,
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(8) + 1,
    },
  },
  logoContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "8px",
    position: "sticky",
    top: 0,
    backgroundColor: "white",
  },
  logo: {
    backgroundImage: `url("${openxSvgNewLogo}")`,
    backgroundSize: "90%",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right",
    flex: 2,
    height: "40px",

    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  logoTitle: {
    fontSize: "14px",
    fontWeight: "500",
    overflow: "hidden",
    lineHeight: 1.5,
    flex: 1,

    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0px 8px",
    // necessary for content to be below app bar
    // ...theme.mixins.toolbar,

    "& .MuiIconButton-root": {
      padding: "8px",
    },

    [theme.breakpoints.down("xs")]: {
      "& .MuiIconButton-root": {
        display: "none",
      },
    },
  },
}));

function SidenavDrawer(props) {
  const {
    dispatch: ReduxDispatcher,
    user = {},
    tabName = "",
    additionalSidenavItems = [],
    sidenavItemsOrderInfo = config.hardCoded.defaultSidenavItemsOrderInfo,
    open,
    handleDrawer,
  } = props;

  const history = useHistory();
  const { t } = useTranslation();
  const classes = useStyles();
  const [isSidenavHovered, setIsSidenavHovered] = useState(false);

  // * Define required event handlers
  const handleWsFormChange = (key, value) => {
    const payload = { key, value };
    const action = updateStandaloneWsForm(payload);
    ReduxDispatcher(action);
  };

  const handleShowWorkbooksDrawer = (value) => {
    const newScreen = { ...initialWsFormScreenValue, allWorkbooks: value };
    handleWsFormChange("screen", newScreen);
  };

  const handleWorkspaceOpenFromSidenav = () => {
    // Google Analytics Event - Master
    masterTrackGaEvent({
      category: "Workspace",
      action: "OpenWorkspaceFromSidenav",
      label: "Open",
    });
    handleShowWorkbooksDrawer(true);
  };

  const toggleAnalyzeManager = () => {
    const pathname = "/analyze/dashboard";
    history.push(pathname);
  };

  const togglePivotManager = () => {
    const pathname = "/pivotx";
    history.push(pathname);
  };

  const toggleWorkspaceManager = () => {
    const pathname = "/workspace";
    history.push(pathname);
  };

  const toggleDatastoryManager = () => {
    const pathname = "/datastory";
    history.push(pathname);
  };

  const toggleAlertsManager = () => {
    const pathname = "/alerts";
    history.push(pathname);
  };

  const toggleReportsManager = () => {
    const pathname = "/reports";
    history.push(pathname);
  };

  const handleOpenOverlay = () => {
    masterTrackGaEvent({
      category: "Menubar",
      action: "FeatureOverlay",
      label: "1",
    });
    const payload = {
      isOpen: true,
      children: <OverlayContainer />,
    };
    const action = updateUserScreen("overlay", payload);
    ReduxDispatcher(action);
  };

  // const toggleReportManager = () => {
  //   // Google Analytics Event - Master
  //   masterTrackGaEvent({
  //     category: "ReportManager",
  //     action: "OpenReportManager",
  //     label: "Sidenav",
  //   });
  //   ReduxDispatcher(
  //     updateUserScreen("reportManager", {
  //       isOpen: true,
  //       metadata: { type: "saved", selections: {} },
  //     })
  //   );
  // };

  // const toggleAlertManager = () => {
  //   const value = {
  //     isOpen: true,
  //   };
  //   ReduxDispatcher(updateUserScreen("alertManager", value));
  // };

  const isVisible = {
    analyze: user.uiFeatureList.analyze && !user.timeFilters.isLoading,
    pivot: user.uiFeatureList.pivot && !user.timeFilters.isLoading,
    datastory: user.uiFeatureList.datastory && !user.timeFilters.isLoading,
    reports: user.uiFeatureList.reportManager && !user.timeFilters.isLoading, //Only when time-filters have been loaded; otherwise the calendar malfunctions (doesn't show all presets)
    alert: user.uiFeatureList.alertManager && !user.timeFilters.isLoading, //Only when time-filters have been loaded; otherwise the calendar malfunctions (doesn't show all presets),
    overlay: user.uiFeatureList.overlayUpdates,
  };

  // const sidenavItemsOrderInfo = config.hardCoded.defaultSidenavItemsOrderInfo;

  const commonTopSidenavData = [
    {
      id: "allWorkbooks",
      name: "My Workspace",
      tooltip: "My Workspace",
      elementId: "allWorkbooksInSidenav",
      iconId: "custom-workspaces-icon",
      customClass: "sidenav-workspaces",
      onClick: () => {
        toggleWorkspaceManager();
      },
      isVisible: true,
    },
    {
      id: "analyze",
      name: "Analyze",
      tooltip: "Analyze",
      elementId: "analyzeToggler",
      iconId: "custom-analyze-icon",
      customClass: "sidenav-analyze",
      onClick: () => {
        toggleAnalyzeManager();
      },
      isVisible: isVisible.analyze,
    },
    {
      id: "pivot",
      name: "Pivot",
      tooltip: "Pivot",
      elementId: "pivotToggler",
      iconId: "custom-pivot-icon",
      customClass: "sidenav-pivot",
      onClick: () => {
        togglePivotManager();
      },
      isVisible: isVisible.pivot,
    },
    {
      id: "datastory",
      name: "Datastory",
      tooltip: "Datastory",
      elementId: "datastoryToggler",
      iconId: "custom-datastory-icon",
      customClass: "sidenav-datastory",
      onClick: () => {
        toggleDatastoryManager();
      },
      isVisible: isVisible.datastory,
    },
    {
      id: "reports",
      name: "Reports",
      tooltip: "Reports",
      elementId: "reportToggle",
      iconId: "custom-report-icon",
      customClass: "sidenav-reports",
      onClick: (user) => toggleReportsManager(user),
      isVisible: isVisible.reports,
    },
    {
      id: "alerts",
      name: "Alerts",
      tooltip: "Alerts",
      elementId: "alertsToggler",
      iconId: "custom-alert-icon",
      customClass: "sidenav-alerts",
      onClick: (user) => toggleAlertsManager(user),
      isVisible: isVisible.alert,
    },
  ];
  const commonBottomSidenavData = [
    // {
    //   id: "settings",
    //   name: "Settings",
    //   tooltip: "Settings",
    //   elementId: "settingsToggler",
    //   iconId: "custom-settings-icon",
    //   customClas: "sidenav-settings",
    //   onClick: () => {},
    //   isVisible: true,
    // },
    // {
    //   id: "admin",
    //   name: "Admin",
    //   tooltip: "Admin",
    //   elementId: "adminToggler",
    //   iconId: "custom-admin-icon",
    //   customClas: "sidenav-admin",
    //   onClick: () => {},
    //   isVisible: true,
    // },
    // {
    //   id: "faq",
    //   name: "FAQ",
    //   tooltip: "FAQ",
    //   elementId: "fagToggler",
    //   iconId: "custom-faq-icon",
    //   customClas: "sidenav-faq",
    //   onClick: () => {},
    //   isVisible: true,
    // },
    // {
    //   id: "overlay",
    //   name: "What's New",
    //   tooltip: "What's New",
    //   elementId: "overlayToggler",
    //   iconId: "custom-overlay-icon",
    //   customClas: "sidenav-overlay",
    //   onClick: () => handleOpenOverlay(),
    //   isVisible: isVisible.overlay,
    // },
  ];
  var finalSidenavData = [...commonTopSidenavData, ...additionalSidenavItems];
  // var finalSidenavData = [...commonSidenavData];
  // Remove unwanted items
  finalSidenavData = finalSidenavData.filter((row) => row.isVisible);
  // Re-order based on sidenavItemsOrderInfo
  finalSidenavData = orderBy(
    finalSidenavData,
    (item) => sidenavItemsOrderInfo[item.id],
    ["asc"]
  );

  return (
    <div className="sidenav-drawer-container">
      <Drawer
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })}
        classes={{
          paper: clsx(classes.paper, {
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          }),
        }}
      >
        <div className="top-panel">
          <div className={classes.logoContainer}>
            <div
              className={`${classes.logo} ${
                !open ? classes.hide : classes.show
              }`}
            ></div>
            {open && <SigviewVR style={{ height: "25px" }} />}
            <div
              className={`${classes.logoTitle} ${
                !open ? classes.hide : classes.show
              }`}
            >
              Sigview
            </div>
            <div className={classes.toolbar}>
              <IconButton size="large" onClick={handleDrawer}>
                {!open ? (
                  <span className="material-icons-round">menu</span>
                ) : (
                  <span className="material-icons-round">close</span>
                )}
              </IconButton>
            </div>
          </div>
          {finalSidenavData.map((row) => (
            <Fragment key={row.id}>
              <SigviewTooltip
                title={!open ? t(row.tooltip) : ""}
                placement="right"
              >
                <div
                  className="sidenav-drawer-item-wrapper"
                  onMouseEnter={() => setIsSidenavHovered(true)}
                  onMouseLeave={() => setIsSidenavHovered(false)}
                >
                  <div
                    onClick={() => row.onClick(user)}
                    className={`sidenav-drawer-item ${row.id} ${
                      tabName + "-" + row.customClass + "-GA"
                    } ${
                      tabName === row.id && !isSidenavHovered ? "active" : ""
                    }`}
                    id={row.elementId}
                  >
                    <div
                      className={`${row.iconId} ${row.id} ${
                        tabName + "-" + row.customClass + "-GA"
                      }`}
                    ></div>
                    <span
                      className={`sidenav-drawer-item-title ${row.id} ${
                        tabName + "-" + row.customClass + "-GA"
                      } ${!open ? classes.hide : classes.show}`}
                    >
                      {t(row.name)}
                    </span>
                  </div>
                </div>
              </SigviewTooltip>
            </Fragment>
          ))}
        </div>
        <div className="bottom-panel">
          {commonBottomSidenavData.map((row) => (
            <Fragment key={row.id}>
              <SigviewTooltip
                title={!open ? t(row.tooltip) : ""}
                placement="right"
              >
                <div
                  className="sidenav-drawer-item-wrapper"
                  onMouseEnter={() => setIsSidenavHovered(true)}
                  onMouseLeave={() => setIsSidenavHovered(false)}
                >
                  <div
                    onClick={() => row.onClick(user)}
                    className={`sidenav-drawer-item ${row.id} ${
                      tabName + "-" + row.customClass + "-GA"
                    } ${
                      tabName === row.id && !isSidenavHovered ? "active" : ""
                    }`}
                    id={row.elementId}
                  >
                    <div
                      className={`${row.iconId} ${row.id} ${
                        tabName + "-" + row.customClass + "-GA"
                      }`}
                    ></div>
                    <span
                      className={`sidenav-drawer-item-title ${row.id} ${
                        tabName + "-" + row.customClass + "-GA"
                      } ${!open ? classes.hide : classes.show}`}
                    >
                      {t(row.name)}
                    </span>
                  </div>
                </div>
              </SigviewTooltip>
            </Fragment>
          ))}
        </div>
      </Drawer>
    </div>
  );
}

SidenavDrawer.propTypes = {
  user: PropTypes.object,
  match: PropTypes.object,
};

const mapStateToProps = (state) => ({
  user: state.user,
  allData: state.data,
});

export default connect(mapStateToProps)(SidenavDrawer);
